import React, { useState } from 'react';
import { Plus, Edit, Trash2, Check, Bell, Settings, Printer, AlertTriangle, X, Save } from 'lucide-react';
import { useData } from '../context/DataContext';
import { toast } from 'sonner';
import PlanningEditor from '../components/PlanningEditor';

import { useT } from '../context/LanguageContext';
import { confirmDialog } from '../lib/confirm';
const dagen = ['Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag', 'Zondag'];
const DEFAULT_WERKPLEKKEN = ['Brug 1', 'Brug 2'];
const WERKSTATUS_KLEUR = {
  'Niet gestart': '#64748b', 'Wacht op onderdelen': '#dc2626', 'Bezig': '#2563eb', 'Klaar': '#16a34a', 'Opgehaald': '#7c3aed',
};
// Lokale ISO-datum (YYYY-MM-DD) zonder UTC-conversie.
const localISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const tijden = Array.from({ length: 48 }, (_, i) => {
  const h = String(Math.floor(i / 2)).padStart(2, '0');
  const m = i % 2 === 0 ? '00' : '30';
  return `${h}:${m}`;
});
const overlaps = (a, b) => new Date(a.start) < new Date(b.eind) && new Date(b.start) < new Date(a.eind);

export default function Planning() {
  const { t } = useT();
  const { planning, klanten, gegevens, deletePlanning, notifyPlanning, updatePlanning, updateGegevens } = useData();
  const [view, setView] = useState('week');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorItem, setEditorItem] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [filterMonteur, setFilterMonteur] = useState('Alle');
  const [filterWerkplek, setFilterWerkplek] = useState('Alle');
  const [showAllHours, setShowAllHours] = useState(false);
  const [dragOverSlot, setDragOverSlot] = useState(null);
  const [printDate, setPrintDate] = useState(localISODate(new Date()));
  const [werkplekkenOpen, setWerkplekkenOpen] = useState(false);

  const werkplekken = (gegevens?.werkplekken && gegevens.werkplekken.length) ? gegevens.werkplekken : DEFAULT_WERKPLEKKEN;

  const weekDates = React.useMemo(() => {
    const now = new Date();
    const day = now.getDay() || 7;
    const monday = new Date(now);
    monday.setDate(now.getDate() - (day - 1) + weekOffset * 7);
    monday.setHours(0, 0, 0, 0);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday); d.setDate(monday.getDate() + i);
      return d;
    });
  }, [weekOffset]);

  const dagLabels = weekDates.map((d, i) => `${t(dagen[i], dagen[i]).slice(0, 3)} ${d.getDate()}/${d.getMonth() + 1}`);

  const visibleTijden = showAllHours ? tijden : tijden.filter(tt => {
    const h = parseInt(tt.split(':')[0], 10);
    return h >= 7 && h <= 19;
  });

  const planningInWeek = React.useMemo(() => planning.filter(p => {
    if (!p.start) return false;
    if (filterMonteur !== 'Alle' && p.monteur !== filterMonteur) return false;
    if (filterWerkplek !== 'Alle' && (p.werkplek || '') !== filterWerkplek) return false;
    const ps = p.start.slice(0, 10);
    return weekDates.some(d => localISODate(d) === ps);
  }), [planning, weekDates, filterMonteur, filterWerkplek]);

  // Capaciteitswaarschuwing: dubbele boeking op dezelfde brug óf dezelfde monteur (overlappend in tijd)
  const conflictIds = React.useMemo(() => {
    const ids = new Set();
    const list = planningInWeek.filter(p => p.start && p.eind);
    for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        const a = list[i], b = list[j];
        if (!overlaps(a, b)) continue;
        if ((a.werkplek && a.werkplek === b.werkplek) || (a.monteur && a.monteur === b.monteur)) { ids.add(a.id); ids.add(b.id); }
      }
    }
    return ids;
  }, [planningInWeek]);

  const monteursList = React.useMemo(() => {
    const s = new Set();
    planning.forEach(p => p.monteur && s.add(p.monteur));
    return Array.from(s);
  }, [planning]);

  const selected = planning.find(p => p.id === selectedId);

  const handleNew = () => { setEditorItem(null); setEditorOpen(true); };
  const handleEdit = (item) => { setEditorItem(item || selected); setEditorOpen(true); };
  const handleDelete = async () => {
    if (!selected) { toast.error(t('Selecteer eerst een afspraak')); return; }
    if (!(await confirmDialog(`${t('Afspraak verwijderen?')} (${selected.titel})`))) return;
    try { await deletePlanning(selected.id); toast.success(t('Afspraak verwijderd')); setSelectedId(null); }
    catch { toast.error(t('Verwijderen mislukt')); }
  };
  const handleNotify = async (kind) => {
    if (!selected) { toast.error(t('Selecteer eerst een afspraak')); return; }
    const klant = klanten.find(k => k.id === selected.klantId);
    const aan = klant?.email || selected.klantEmail;
    if (!aan) { toast.error(t('Klant heeft geen e-mailadres')); return; }
    const label = kind === 'herinnering' ? t('Herinnering verstuurd') : t('Bevestiging verstuurd');
    try {
      const res = await notifyPlanning(selected.id, { kind, aan });
      const isReal = !String(res.status || '').toUpperCase().includes('MOCK');
      if (isReal) toast.success(label, { description: `${t('Aan')}: ${aan}` });
      else toast.warning(t('E-mail niet geconfigureerd'), { description: res.status });
    } catch (err) { toast.error(t('Versturen mislukt'), { description: err.response?.data?.detail || err.message }); }
  };

  // Sleepbare kalender: verplaats afspraak naar nieuw slot (behoud duur)
  const handleDrop = async (id, dayIdx, tijd) => {
    setDragOverSlot(null);
    const ev = planning.find(p => p.id === id);
    if (!ev || !ev.start) return;
    const duur = Math.max(15, Math.round((new Date(ev.eind) - new Date(ev.start)) / 60000) || 60);
    const nieuweStart = `${localISODate(weekDates[dayIdx])}T${tijd}`;
    const ed = new Date(nieuweStart); ed.setMinutes(ed.getMinutes() + duur);
    const nieuweEind = `${localISODate(ed)}T${String(ed.getHours()).padStart(2, '0')}:${String(ed.getMinutes()).padStart(2, '0')}`;
    if (nieuweStart === ev.start) return;
    try {
      await updatePlanning(id, { ...ev, start: nieuweStart, eind: nieuweEind });
      toast.success(t('Afspraak verplaatst', 'Afspraak verplaatst'), { description: `${ev.titel} → ${tijd}` });
    } catch { toast.error(t('Verplaatsen mislukt', 'Verplaatsen mislukt')); }
  };

  const byDay = React.useMemo(() => {
    const map = {};
    weekDates.forEach((d, i) => {
      const iso = localISODate(d);
      map[i] = planningInWeek.filter(p => p.start && p.start.startsWith(iso));
    });
    return map;
  }, [weekDates, planningInWeek]);

  const eventInSlot = (dayIdx, tijd) => (byDay[dayIdx] || []).find(e => e.start && e.start.slice(11, 16) === tijd);

  const printDagplanning = () => {
    const items = planning.filter(p => p.start && p.start.slice(0, 10) === printDate)
      .sort((a, b) => (a.start || '').localeCompare(b.start || ''));
    const datLabel = new Date(printDate + 'T00:00').toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    const groepen = [...werkplekken, t('Geen werkplek', 'Geen werkplek')];
    const esc = (s) => String(s || '').replace(/</g, '&lt;');
    const rij = (p) => {
      const klant = klanten.find(k => k.id === p.klantId);
      const tijd = `${(p.start || '').slice(11, 16)}–${(p.eind || '').slice(11, 16)}`;
      return `<tr><td class="tijd">${tijd}</td><td><b>${esc(p.titel)}</b></td><td>${esc(klant?.naam || p.klantNaam || '-')}</td><td class="mono">${esc(p.kenteken)}</td><td>${esc(p.monteur || '-')}</td><td>${esc(p.werkStatus || '-')}</td></tr>`;
    };
    const blok = (g) => {
      const list = items.filter(p => (p.werkplek || t('Geen werkplek', 'Geen werkplek')) === g);
      if (!list.length) return '';
      return `<h3>${esc(g)} <span class="n">(${list.length})</span></h3>
        <table><thead><tr><th>${t('Tijd', 'Tijd')}</th><th>${t('Titel', 'Titel')}</th><th>${t('Klant', 'Klant')}</th><th>${t('Kenteken', 'Kenteken')}</th><th>${t('Monteur', 'Monteur')}</th><th>${t('Werkstatus', 'Werkstatus')}</th></tr></thead><tbody>${list.map(rij).join('')}</tbody></table>`;
    };
    const body = groepen.map(blok).join('') || `<p class="leeg">${t('Geen afspraken op deze dag.', 'Geen afspraken op deze dag.')}</p>`;
    const w = window.open('', '_blank', 'width=900,height=1000');
    if (!w) return;
    w.document.write(`<!doctype html><html><head><title>Dagplanning ${printDate}</title>
      <style>
        @page { size: A4; margin: 14mm; }
        *{box-sizing:border-box;font-family:-apple-system,Segoe UI,Roboto,sans-serif;}
        body{margin:0;color:#0f172a;}
        h1{font-size:20px;margin:0 0 2px;} .sub{color:#64748b;font-size:13px;margin-bottom:18px;}
        h3{font-size:14px;margin:18px 0 6px;border-bottom:2px solid #0f172a;padding-bottom:3px;} .n{color:#94a3b8;font-weight:400;}
        table{width:100%;border-collapse:collapse;font-size:12.5px;} th{text-align:left;background:#f1f5f9;padding:6px 8px;}
        td{padding:6px 8px;border-bottom:1px solid #e2e8f0;} .tijd,.mono{font-family:ui-monospace,monospace;white-space:nowrap;}
        .leeg{color:#94a3b8;} .garage{margin-top:24px;font-size:11px;color:#94a3b8;}
      </style></head><body>
      <h1>${t('Dagplanning', 'Dagplanning')}</h1>
      <div class="sub">${esc(datLabel)} · ${esc(gegevens?.bedrijfsnaam || gegevens?.naam || 'MonteurCentraal')}</div>
      ${body}
      <div class="garage">${t('Afgedrukt via MonteurCentraal', 'Afgedrukt via MonteurCentraal')}</div>
      <script>window.onload=function(){setTimeout(function(){window.print();},250);};<\/script>
      </body></html>`);
    w.document.close();
  };

  return (
    <div>
      <div className="cs-page-header">
        <div><h2>{t('page.planning.title')}</h2><p>{planning.length} {t('afspraken', 'afspraken')}</p></div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="cs-btn primary" onClick={handleNew} data-testid="planning-new"><Plus size={14} /> {t('Nieuwe afspraak')}</button>
          <button className="cs-btn" onClick={() => handleEdit()} disabled={!selected}><Edit size={14} /> {t('Bewerken')}</button>
          <button className="cs-btn" onClick={() => handleNotify('bevestiging')} disabled={!selected}><Check size={14} /> {t('Bevestiging', 'Bevestiging')}</button>
          <button className="cs-btn" onClick={() => handleNotify('herinnering')} disabled={!selected}><Bell size={14} /> {t('Herinnering', 'Herinnering')}</button>
          <button className="cs-btn danger" onClick={handleDelete} disabled={!selected}><Trash2 size={14} /> {t('Verwijderen')}</button>
        </div>
      </div>

      <div className="cs-tabs">
        <button className={`cs-tab ${view === 'week' ? 'active' : ''}`} onClick={() => setView('week')}>{t('Weekagenda', 'Weekagenda')}</button>
        <button className={`cs-tab ${view === 'lijst' ? 'active' : ''}`} onClick={() => setView('lijst')}>{t('Lijstweergave', 'Lijstweergave')}</button>
      </div>

      {/* Capaciteitswaarschuwing */}
      {conflictIds.size > 0 && (
        <div data-testid="planning-capaciteit-warn" style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: 13.5, fontWeight: 600 }}>
          <AlertTriangle size={16} /> {conflictIds.size} {t('afspraken met een dubbele boeking (zelfde brug of monteur op hetzelfde moment). Controleer de capaciteit.', 'afspraken met een dubbele boeking (zelfde brug of monteur op hetzelfde moment). Controleer de capaciteit.')}
        </div>
      )}

      {view === 'week' ? (
        <div className="cs-card padded">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 8, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button className="cs-btn ghost" onClick={() => setWeekOffset(o => o - 1)}>◀</button>
              <button className="cs-btn" onClick={() => setWeekOffset(0)}>{t('Deze week')}</button>
              <button className="cs-btn ghost" onClick={() => setWeekOffset(o => o + 1)}>▶</button>
              <span style={{ fontSize: 12, color: 'var(--cs-text-muted)', marginLeft: 8 }}>
                {weekDates[0].toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })} – {weekDates[6].toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ fontSize: 12, color: 'var(--cs-text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                <input type="checkbox" checked={showAllHours} onChange={e => setShowAllHours(e.target.checked)} data-testid="planning-24h" />
                {t('24/7 uren tonen', '24/7 uren tonen')}
              </label>
              <select className="cs-input" style={{ width: 130 }} value={filterWerkplek} onChange={e => setFilterWerkplek(e.target.value)} data-testid="planning-filter-werkplek">
                <option value="Alle">{t('Alle bruggen', 'Alle bruggen')}</option>
                {werkplekken.map(w => <option key={w} value={w}>{w}</option>)}
              </select>
              {monteursList.length > 0 && (
                <select className="cs-input" style={{ width: 140 }} value={filterMonteur} onChange={e => setFilterMonteur(e.target.value)}>
                  <option value="Alle">{t('Alle monteurs', 'Alle monteurs')}</option>
                  {monteursList.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              )}
              <input type="date" className="cs-input" style={{ width: 150 }} value={printDate} onChange={e => setPrintDate(e.target.value)} data-testid="planning-print-date" />
              <button className="cs-btn" onClick={printDagplanning} data-testid="planning-print"><Printer size={14} /> {t('Dagplanning', 'Dagplanning')}</button>
              <button className="cs-btn ghost" onClick={() => setWerkplekkenOpen(true)} data-testid="planning-werkplekken-open" title={t('Bruggen beheren', 'Bruggen beheren')}><Settings size={14} /></button>
            </div>
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--cs-text-muted)', marginBottom: 6 }}>{t('Tip: sleep een afspraak naar een ander tijdslot om te verplaatsen.', 'Tip: sleep een afspraak naar een ander tijdslot om te verplaatsen.')}</div>
          <div className="cs-calendar" style={{ maxHeight: 640, overflowY: 'auto' }}>
            <div className="head">{t('Tijd')}</div>
            {dagLabels.map(d => <div key={d} className="head">{d}</div>)}
            {visibleTijden.map(tijd => (
              <React.Fragment key={tijd}>
                <div className="time">{tijd}</div>
                {weekDates.map((_, dayIdx) => {
                  const ev = eventInSlot(dayIdx, tijd);
                  const slotKey = dayIdx + tijd;
                  const isConflict = ev && conflictIds.has(ev.id);
                  const wkleur = ev ? (WERKSTATUS_KLEUR[ev.werkStatus] || '#64748b') : '#64748b';
                  return (
                    <div key={slotKey} className="slot"
                      onDragOver={(e) => { e.preventDefault(); if (dragOverSlot !== slotKey) setDragOverSlot(slotKey); }}
                      onDragLeave={() => setDragOverSlot(s => s === slotKey ? null : s)}
                      onDrop={(e) => { e.preventDefault(); const id = e.dataTransfer.getData('text/plain'); if (id) handleDrop(id, dayIdx, tijd); }}
                      style={dragOverSlot === slotKey ? { background: '#dbeafe', outline: '2px dashed #2563eb' } : undefined}>
                      {ev && (
                        <div className={`event ${ev.status === 'Bevestigd' ? 'confirmed' : ev.status === 'In uitvoering' ? 'busy' : ''}`}
                          draggable
                          onDragStart={(e) => e.dataTransfer.setData('text/plain', ev.id)}
                          title={`${ev.titel}${ev.werkplek ? ' · ' + ev.werkplek : ''}${ev.werkStatus ? ' · ' + ev.werkStatus : ''}`}
                          onClick={() => { setSelectedId(ev.id); handleEdit(ev); }}
                          data-testid={`planning-event-${ev.id}`}
                          style={isConflict ? { outline: '2px solid #dc2626' } : undefined}>
                          <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ width: 7, height: 7, borderRadius: '50%', background: wkleur, flexShrink: 0 }} />
                            {isConflict && <AlertTriangle size={11} color="#dc2626" />}
                            {ev.titel}
                          </div>
                          <div style={{ color: 'var(--cs-text-muted)' }}>{ev.kenteken} · {ev.monteur}</div>
                          {ev.werkplek && <div style={{ fontSize: 10, fontWeight: 700, color: '#7c3aed' }}>{ev.werkplek}</div>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      ) : (
        <div className="cs-card">
          <table className="cs-table">
            <thead><tr><th>{t('Start')}</th><th>{t('Duur')}</th><th>{t('Titel')}</th><th>{t('Klant')}</th><th>{t('Kenteken')}</th><th>{t('Werkplek / brug', 'Werkplek / brug')}</th><th>{t('Monteur')}</th><th>{t('Werkstatus', 'Werkstatus')}</th><th>{t('Status')}</th></tr></thead>
            <tbody>
              {planning.map(p => {
                const klant = klanten.find(k => k.id === p.klantId);
                const sd = new Date(p.start), ed = new Date(p.eind);
                const duur = Math.round((ed - sd) / 60000);
                return (
                  <tr key={p.id} className={p.id === selectedId ? 'selected' : ''} onClick={() => setSelectedId(p.id)} onDoubleClick={() => handleEdit(p)} data-testid={`planning-row-${p.id}`}>
                    <td>{sd.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' })} {sd.toTimeString().slice(0, 5)}</td>
                    <td>{duur} min</td>
                    <td style={{ fontWeight: 600 }}>{p.titel}</td>
                    <td>{klant?.naam || p.klantNaam || '-'}</td>
                    <td style={{ fontFamily: 'ui-monospace, monospace' }}>{p.kenteken}</td>
                    <td>{p.werkplek ? <span className="cs-badge" style={{ background: '#f3e8ff', color: '#7c3aed' }}>{p.werkplek}</span> : '—'}</td>
                    <td><span className="cs-badge gray">{p.monteur}</span></td>
                    <td>{p.werkStatus ? <span className="cs-badge" style={{ background: `${WERKSTATUS_KLEUR[p.werkStatus] || '#64748b'}22`, color: WERKSTATUS_KLEUR[p.werkStatus] || '#64748b' }}>{t(p.werkStatus, p.werkStatus)}</span> : '—'}</td>
                    <td><span className={`cs-badge ${p.status === 'Aangevraagd' ? 'red' : p.status === 'Bevestigd' ? 'green' : p.status === 'In uitvoering' ? 'amber' : 'blue'}`}>{t(p.status, p.status)}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editorOpen && <PlanningEditor afspraak={editorItem} onClose={() => { setEditorOpen(false); setEditorItem(null); }} />}
      {werkplekkenOpen && <WerkplekkenModal werkplekken={werkplekken} onSave={(lst) => { updateGegevens({ werkplekken: lst }); setWerkplekkenOpen(false); toast.success(t('Bruggen opgeslagen', 'Bruggen opgeslagen')); }} onClose={() => setWerkplekkenOpen(false)} t={t} />}
    </div>
  );
}

function WerkplekkenModal({ werkplekken, onSave, onClose, t }) {
  const [lst, setLst] = useState(werkplekken);
  const [inp, setInp] = useState('');
  const add = () => { const v = inp.trim(); if (!v) return; setLst([...lst, v]); setInp(''); };
  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }} data-testid="werkplekken-modal">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>{t('Bruggen / werkplekken', 'Bruggen / werkplekken')}</h3>
          <button className="cs-btn ghost" onClick={onClose} data-testid="wp-close"><X size={16} /></button>
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input className="cs-input" placeholder={t('Bijv. Brug 3 of Buitenplaats', 'Bijv. Brug 3 of Buitenplaats')} value={inp} onChange={e => setInp(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} data-testid="wp-input" />
          <button className="cs-btn" onClick={add} data-testid="wp-add"><Plus size={14} /></button>
        </div>
        <div className="space-y-2">
          {lst.length === 0 && <div style={{ fontSize: 13, color: '#94a3b8' }}>{t('Nog geen werkplekken', 'Nog geen werkplekken')}</div>}
          {lst.map((w, i) => (
            <div key={i} data-testid={`wp-item-${i}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px' }}>
              <span style={{ fontWeight: 600 }}>{w}</span>
              <button onClick={() => setLst(lst.filter((_, idx) => idx !== i))} data-testid={`wp-del-${i}`} style={{ border: 'none', background: 'none', color: '#dc2626', cursor: 'pointer', display: 'flex' }}><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button className="cs-btn" onClick={onClose}>{t('Annuleren')}</button>
          <button className="cs-btn primary" onClick={() => onSave(lst)} data-testid="wp-save"><Save size={14} /> {t('Opslaan')}</button>
        </div>
      </div>
    </div>
  );
}
